import { el, setChildren, setStyle } from "redom"
import { openUserPageOnClick } from "../pages/userpage.js"
import { PATH } from "../helpers/shared-record-path.js"
import { playRecord, openRecordOnClick, type CurrentMusicInfo } from "../pages/recordpage.js"
import { updateToPost } from "../helpers/collapse.js"
import { hostAppCall } from '../../utils/appFunctions.js'
import app from "../../utils/app.js"
import 'mediaelement/build/mediaelementplayer.min.css';
import 'mediaelement/build/mediaelement-and-player.min.js';

declare class MediaElementPlayer {
    media: HTMLMediaElement;
    node: HTMLMediaElement;
    controls: HTMLElement;
    currentPost?: string;
    currentTime: number;
    play: () => void;
    pause: () => void;
    setCurrentTime: (time: number) => void;
    constructor(id: string, options: any);
}
  
const moveToPreviousRecord = (info: CurrentMusicInfo) => moveToAnotherRecord(info, "previous")
const moveToAnotherRecord = (info: CurrentMusicInfo, direction: string) => {
    const getNextIndex = (index: number, increasedBy: number, length: number): number => (index + increasedBy + length) % length
    const isPopupShown: boolean = document.getElementsByClassName("modal-backdrop").length != 0

    // It won't move to the next record, if 1) the playlist doesn't exist, or 2) audio player is not shown, or a 3) pop-up is shown.
    if (Audio.playlist == null || info == null || isPopupShown) return
    // It allows to move to the next record without a playlist, only when the user stays in the record page(which means it has 'UP NEXT').
    if (info.playlistType == "default" && !PATH.isRecordPage()) return

    const index: number = Audio.playlist.indexOf(info.record.id)
    const increasedBy: number = direction == "previous" ? -1 : 1
    const nextIndex: number = getNextIndex(index, increasedBy, Audio.playlist.length)
    const nextRecordId: string = Audio.playlist[nextIndex]
    const nextRecordUrl: string = '/r/' + nextRecordId + info.param
    if (PATH.isRecordPage()) openRecordOnClick(nextRecordUrl)
    else playRecord(nextRecordUrl)
}

Object.assign(app.featureActivations, {
    "callbackAppOnAudioPlayerToggle": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 240715 },
    "callbackAppOnAudioPlayerTogglev2": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 250815 }
});

export interface RecordInfo {
    id: string;
    param: string;
}
export class AudioManager {
    private readonly autoplayId: string = "autoplay"
    isPlaying: boolean = false
    player?: MediaElementPlayer
    private info?: CurrentMusicInfo
    recordInfo?: RecordInfo
    playlist?: string[]
    init = (info: CurrentMusicInfo) => {
        this.info = info
        // Note: Audio player has '(in promise) DOMException issue'. The player on the mejs homepage(https://www.mediaelementjs.com/) also shows the same error.
        // The error occurs when clicking on the progress bar in audio player while music is playing. But it causes nothing to user.
        // The keyword to use is "Uncaught: (in promise) DOMException issue media element js" on Google.

        const playable = this.info.record.status == "OK" || this.info.record.status == "Preprocessing"

        // In the case an audio player exists, if the player's record and the requested record are the same,
        // 1. Reset the player's navigator, as the requested record could be in the list or remove all the list.
        // 2. Restart the recent record if it is currently paused.
        if (this.player?.media.src == this.info.record.url && playable) {
            setPlayerNavigator(this.info.playlistType)
            if (!this.isPlaying) this.toggle()
        }
        // In the case there is no audio player or the player's record and the requested record are different, create an audio player.
        if ((this.player && this.info.record.url != this.player.media.src) || !this.player) {
            this.player = createAudioPlayer(this.info)
            if (playable) {
                this.player.currentPost = this.info.record.id
                this.player.controls.style.background = "#212529" // changing the player color
                document.getElementsByClassName("mejs__playpause-button")[0].remove() //removing the play button in audio player at the bottom
                this.player.media.src = this.info.record.url
            }
            else this.hidePlayer()
        }
    }
    moveToPrevious = () => {
        if(!this.player) return
        if (this.player.currentTime < 5 && this.info) moveToPreviousRecord(this.info)
        else this.player.currentTime = 0.0
    }
    moveToNextRecord = () => {
         if (this.info) moveToAnotherRecord(this.info, "next")
    }
    isAutoPlay = (): boolean => {
        if (localStorage.getItem(this.autoplayId) == null) localStorage.setItem(this.autoplayId, "checked")
        return localStorage.getItem(this.autoplayId) == "checked"
    }
    toggleAutoPlay = () => {
        localStorage.setItem(this.autoplayId, (localStorage.getItem(this.autoplayId) == "checked") ? "unchecked" : "checked")
    }
    displayPauseIcon = () => {
        this.isPlaying = true
        this.updateImages()
    }
    displayPlayIcon = () => {
        this.isPlaying = false
        this.updateImages()
    }
    toggle = () => {
        if ((app.isAllowedTo("callbackAppOnAudioPlayerToggle"))) hostAppCall(app.isAllowedTo("callbackAppOnAudioPlayerTogglev2") ? "toggleHandler" : "toggleHanlder", this.isPlaying ? "false" : "true")
        this.toggleWithoutCallback()
    }
    toggleWithoutCallback = () => {
        if (this.isPlaying) this.player?.pause() 
        else this.player?.play()
    }
    updateImages = () => {
        const isPlaying = this.isPlaying
        Array.from(document.getElementsByClassName("playBtn")).forEach(btn => {
          btn.classList.toggle("fa-pause", isPlaying);
          btn.classList.toggle("fa-play", !isPlaying);
        });
        const nowPlayingImage = document.getElementById("nowPlayingImage");
        if (nowPlayingImage) nowPlayingImage.style.padding = isPlaying ? "0px" : "31px"; //Padding is 31 to remove the image in the src         
    }
    get audioElement(): HTMLAudioElement | undefined {
        return document.getElementById("audio") as HTMLAudioElement
    }
    hidePlayer = () => {
        const audioElement = this.audioElement
        if (!audioElement) return;
        setStyle(audioElement, { display: "none" })
        audioElement.innerHTML = ""
    }
    displayPlayer = () => {
        const audioElement = this.audioElement
        if (!audioElement) return;
        setStyle(audioElement, { display: "block" })
        const bottomContainer = document.getElementById("bottomContainer")
        if (bottomContainer) bottomContainer.style.paddingBottom = "0px"
    }
}
export const Audio = new AudioManager()

const setPlayerNavigator = (type: string) => {
    const isList: boolean = type != "default"
    Array.from(document.getElementsByClassName("playerNavigator") as HTMLCollectionOf<HTMLElement>).forEach(el => {
        if (isList) {
            el.style.removeProperty("display");
        } else {
            el.style.display = "none";
        }
    });
}

const createBottomPlayer = (info: CurrentMusicInfo): HTMLAudioElement => {
    const emptyCell = () => el("td", { style: "margin: 0; padding: 0; width: 15px; border-top: 0;" })
    const playButton = (action: Function, classAttr: string) =>
        el("td", { className: "align-middle clickable highlight-only-when-touched", align: "right", style: "border-top: 0px; width: 30px; padding: 10px 20px 0 20px;", onclick: action },
            el("i", { className: `audioPlayer playBtn naviBtn fas fa-${classAttr}`, style: "font-size: 30px; padding-top: 0; color: #fff;" })
        )
    const navigationButton = (action: Function, classAttr: string) =>
        el("td", { className: "playerNavigator align-middle clickable highlight-only-when-touched", align: "right", style: "border-top: 0px; width: 30px; padding: 10px 5px 0 5px; display: none;", onclick: action },
            el("i", { className: `audioPlayer naviBtn fas fa-fast-${classAttr}`, style: "font-size: 20px; padding-top: 0; color: #fff;" })
        )

    const userImage = el("td", { style: "border-top: 0px; width: 60px; padding: 15px 0 0 0;" },
        el("img", { class: "clickable", src: info.uploader.imgUrl, style: "height: 60px; width: 60px; border-radius: 5px;", onclick: () => openUserPageOnClick(info.uploader.id), onerror: (e: Event) => { (e.target as HTMLImageElement).src = "/public/img/icon200x200.png" } }))
    const songInfo = el("td", { class: "align-middle clickable", style: "border-top: 0px; padding: 10px 10px 0 10px; border-bottom: 0px; max-width: calc(100vw - 210px);", onclick: () => Audio.recordInfo && updateToPost(Audio.recordInfo.id, Audio.recordInfo.param.replace("?list=", "")) }, // max-width must be set to show '...' in record's title.
        el("p", { id: "audioPlayerTitle", style: "color:#ffffff; font-size:larger; margin-bottom:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-left: 0px;" }, info.record.title),
        el("p", { style: "color:#c5c5c5; font-size:smaller; margin-bottom:0; line-height:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis" }, info.uploader.name))

    const nowPlaying = el("tr", { id: "nowPlaying" },
        emptyCell(),
        userImage,
        songInfo,
        navigationButton(() => Audio.moveToPrevious(), "backward"),
        playButton(() => Audio.toggle(), Audio.isPlaying ? "pause" : "play"),
        navigationButton(() => Audio.moveToNextRecord(), "forward"),
        emptyCell())

    const audioElement = document.getElementById("audio")
    const audioPlayerEl =  el("audio", { autoplay: true, id: "audioPlayer", preload: "none", controls: "controls", style: "max-width: 100%" })
    if(audioElement){
        setChildren(audioElement, [
        el("table.audioplayer table", { style: "margin: 0px" }, nowPlaying),
        el("div.audio-player", audioPlayerEl)
        ])
    }
    Audio.displayPlayer()
    setPlayerNavigator(info.playlistType)
    return audioPlayerEl
}

const createAudioPlayer = (info: CurrentMusicInfo): MediaElementPlayer => {
    const audioEl = createBottomPlayer(info)

    if (audioEl) {
        var isPlaying = false;

        audioEl.onplaying = () => {
            isPlaying = true;
        }
        audioEl.onpause = function() {
            isPlaying = false;  
        };

        const originalPause = audioEl.pause.bind(audioEl);
        audioEl.pause = function() {    
            if(!audioEl.paused && isPlaying) {
                isPlaying = false
                originalPause();
            }
        }

        const originalPlay = audioEl.play.bind(audioEl);
        audioEl.play = function() {
            if(!audioEl.paused && isPlaying) return Promise.resolve();
            const playPromise = originalPlay();
            return playPromise;
        };
    }
    
    return new MediaElementPlayer(audioEl.id, {
       success: (mediaElement: HTMLMediaElement, domObject: HTMLAudioElement) => {
           mediaElement.addEventListener('play', Audio.displayPauseIcon, false)
           mediaElement.addEventListener('playing', Audio.displayPauseIcon, false)
           mediaElement.addEventListener('ended', () => {
               Audio.displayPlayIcon()
               if (Audio.isAutoPlay() && document.activeElement?.id !== "commentText")
                   Audio.moveToNextRecord()
           }, false)
           mediaElement.addEventListener('pause', Audio.displayPlayIcon)
            mediaElement.addEventListener('timeupdate', () => {
                const millis = Math.round(mediaElement.currentTime * 1000)
                hostAppCall("updateCurrentMillis", millis.toString())
            })
        }
   })
}
